import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const quickAddItems = [
  { label: 'Yeni Müşteri', emoji: '👥', path: '/customers' },
  { label: 'Yeni Fırsat',  emoji: '💼', path: '/deals'     },
  { label: 'Yeni Görev',  emoji: '✅', path: '/tasks'     },
];

const mockNotifications = [
  { id: 1, text: 'Yeni müşteri eklendi', time: '5 dk once', read: false },
  { id: 2, text: 'Deal asama guncellendi', time: '1 saat once', read: false },
  { id: 3, text: 'Görev suresi doldu', time: '2 saat once', read: true },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [unread, setUnread] = useState(2);
  const searchRef = useRef(null);
  const quickAddRef = useRef(null);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (quickAddRef.current && !quickAddRef.current.contains(e.target)) setShowQuickAdd(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setShowResults(false); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/customers', { params: { search: query } });
        setResults(res.data.slice(0, 6));
        setShowResults(true);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const roleLabels = { super_admin: 'Super Admin', office_sales: 'Ofis Satış', field_sales: 'Saha Satış' };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
      <div className="flex items-center gap-3 px-4 md:px-6 h-16">

        <div className="flex-1 max-w-xl relative" ref={searchRef}>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              onFocus={() => results.length > 0 && setShowResults(true)}
              placeholder="Müşteri, firma veya telefon ara..."
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white focus:border-transparent placeholder-gray-400 transition-all"/>
            {searching && (
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            )}
          </div>
          {showResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden animate-fadein z-50">
              {results.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400"><div className="text-2xl mb-2">🔍</div>Sonuç bulunamadı</div>
              ) : (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">Müşteriler ({results.length})</div>
                  {results.map(c => (
                    <button key={c.id} onClick={() => { navigate('/customers/' + c.id); setQuery(''); setShowResults(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm flex-shrink-0">{c.full_name[0].toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-800 truncate">{c.full_name}</div>
                        <div className="text-xs text-gray-400 truncate">{[c.company_name, c.phone, c.city].filter(Boolean).join(' · ')}</div>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">

          <div className="relative" ref={notifRef}>
            <button onClick={() => { setShowNotifs(v => !v); setShowQuickAdd(false); setShowProfile(false); }}
              className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread}</span>}
            </button>
            {showNotifs && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden animate-fadein z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <span className="font-bold text-sm text-gray-800">Bildirimler</span>
                  {unread > 0 && <button onClick={() => setUnread(0)} className="text-xs text-indigo-600 font-semibold">Tümünü okundu işaretle</button>}
                </div>
                <div className="divide-y divide-gray-50">
                  {mockNotifications.map(n => (
                    <div key={n.id} className={"flex items-start gap-3 px-4 py-3 " + (n.read ? 'hover:bg-gray-50' : 'bg-indigo-50/50')}>
                      <div className={"w-2 h-2 rounded-full mt-1.5 flex-shrink-0 " + (n.read ? 'bg-gray-200' : 'bg-indigo-500')}/>
                      <div><p className="text-sm text-gray-700">{n.text}</p><p className="text-xs text-gray-400 mt-0.5">{n.time}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={quickAddRef}>
            <button onClick={() => { setShowQuickAdd(v => !v); setShowNotifs(false); setShowProfile(false); }}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-3.5 py-2 rounded-xl transition-all shadow-sm hover:shadow-md active:scale-95">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
              <span className="hidden sm:inline">Hızlı Ekle</span>
            </button>
            {showQuickAdd && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden animate-fadein z-50">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Ne eklemek istersiniz?</span>
                </div>
                {quickAddItems.map(item => (
                  <button key={item.path} onClick={() => { navigate(item.path); setShowQuickAdd(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left">
                    <span className="text-lg">{item.emoji}</span>
                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={profileRef}>
            <button onClick={() => { setShowProfile(v => !v); setShowQuickAdd(false); setShowNotifs(false); }}
              className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">{user?.full_name?.[0]?.toUpperCase() || 'U'}</div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-semibold text-gray-800 leading-tight">{user?.full_name}</div>
                <div className="text-xs text-gray-400">{roleLabels[user?.role]}</div>
              </div>
              <svg className="w-4 h-4 text-gray-400 hidden md:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            {showProfile && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden animate-fadein z-50">
                <div className="px-4 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold">{user?.full_name?.[0]?.toUpperCase()}</div>
                  <div>
                    <div className="font-semibold text-sm text-gray-800">{user?.full_name}</div>
                    <div className="text-xs text-gray-400">{user?.email}</div>
                  </div>
                </div>
                <div className="p-2 border-t border-gray-100">
                  <button onClick={() => { logout(); window.location.href = '/login'; }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 transition-colors font-medium">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                    </svg>
                    Çıkış Yap
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}
